/**
 * Church Demand Intelligence — Landing Page
 *
 * Index route for /church-demand. Renders 4 KPI cards and a placeholder for the
 * bird's-eye map (Task 5.4 will build the real map).
 *
 * Data sources:
 *   - churchDemandAPI.summary() — total_clusters_by_trust_level + new_this_week_count
 *   - churchDemandAPI.clusters({ limit: 200 }) — derive top denomination and top state
 *     client-side since /summary does not return top-N aggregates
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { churchDemandAPI } from '../../../services/api';
import KPICard from '../../../components/founder-dashboard/KPICard';
import DemandMap from './components/DemandMap';
import {
  BuildingLibraryIcon,
  SparklesIcon,
  TagIcon,
  MapIcon,
} from '@heroicons/react/24/outline';

// Reduce an array of cluster rows to the most frequent non-null value for a given key.
// Returns { value, count } or { value: null, count: 0 } if no non-null entries.
function topValue(rows, key) {
  const counts = new Map();
  for (const row of rows) {
    const v = row?.[key];
    if (v === null || v === undefined || v === '') continue;
    counts.set(v, (counts.get(v) || 0) + 1);
  }
  if (counts.size === 0) return { value: null, count: 0 };
  let best = null;
  let bestCount = 0;
  for (const [v, c] of counts) {
    if (c > bestCount) {
      best = v;
      bestCount = c;
    }
  }
  return { value: best, count: bestCount };
}

export default function ChurchDemandLanding() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [clusters, setClusters] = useState([]);
  const [topDenom, setTopDenom] = useState({ value: null, count: 0 });
  const [topState, setTopState] = useState({ value: null, count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [summaryRes, clustersRes] = await Promise.all([
          churchDemandAPI.summary(),
          churchDemandAPI.clusters({ limit: 200 }),
        ]);

        if (cancelled) return;

        setSummary(summaryRes.data);
        const rows = clustersRes.data?.clusters || [];
        setClusters(rows);
        setTopDenom(topValue(rows, 'denomination'));
        setTopState(topValue(rows, 'state'));
      } catch (err) {
        if (cancelled) return;
        const message =
          err?.response?.data?.error ||
          err?.message ||
          'Failed to load church demand summary';
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const totalClusters = summary
    ? Object.values(summary.total_clusters_by_trust_level || {}).reduce(
        (sum, n) => sum + (n || 0),
        0
      )
    : null;

  const newThisWeek = summary?.new_this_week_count ?? null;

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-3 text-sm">
          <strong className="font-semibold">Failed to load:</strong> {error}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Clusters"
          value={totalClusters}
          icon={BuildingLibraryIcon}
          loading={loading}
          subtitle="Across all trust levels"
        />
        <KPICard
          title="New This Week"
          value={newThisWeek}
          icon={SparklesIcon}
          loading={loading}
          subtitle="First-seen in last 7 days"
        />
        <KPICard
          title="Top Denomination"
          value={topDenom.value || '—'}
          icon={TagIcon}
          loading={loading}
          subtitle={
            topDenom.count > 0
              ? `${topDenom.count} cluster${topDenom.count === 1 ? '' : 's'}`
              : 'No data yet'
          }
        />
        <KPICard
          title="Top State"
          value={topState.value || '—'}
          icon={MapIcon}
          loading={loading}
          subtitle={
            topState.count > 0
              ? `${topState.count} cluster${topState.count === 1 ? '' : 's'}`
              : 'No data yet'
          }
        />
      </div>

      <DemandMap
        clusters={clusters}
        onPinClick={(cluster) => navigate(`/church-demand/clusters/${cluster.id}`)}
      />
    </div>
  );
}
